import { cn } from "@/lib/utils";
import { usePublicSettings } from "@/hooks/usePublicSettings";

interface LogoProps {
  variant?: 'full' | 'mark' | 'compact';
  className?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  loading?: 'lazy' | 'eager';
}

export function Logo({ variant = 'full', className, size = 'md', loading = 'eager' }: LogoProps) {
  const { getSetting } = usePublicSettings();
  const logoUrl = getSetting('logo_url', '/chesshatch-logo.png');
  
  const sizeConfig = {
    sm: 'h-12',
    md: 'h-16',
    lg: 'h-24',
    xl: 'h-32'
  };

  const paddingConfig = {
    sm: 'p-2',
    md: 'p-3',
    lg: 'p-4',
    xl: 'p-5'
  };

  return (
    <div className={cn("flex items-center", className)}>
      <div className={cn(
        "rounded-full",
        paddingConfig[size],
        // Light mode: teal gradient background with soft glow
        "bg-gradient-to-br from-teal-100 to-emerald-100",
        "shadow-lg shadow-teal-200/50",
        // Dark mode: completely transparent with no effects
        "dark:bg-transparent dark:shadow-none dark:p-0"
      )}>
        {/* White layer for blend mode to work correctly */}
        <div className="rounded-full bg-white dark:bg-transparent">
          <img 
            src={logoUrl}
            alt="ChessHatch Logo"
            loading={loading}
            className={cn(
              sizeConfig[size], 
              "w-auto object-contain",
              // Remove black background using blend mode
              "mix-blend-multiply",
              // Teal overlay filter for light mode only
              "hue-rotate-[140deg] saturate-150 brightness-95",
              // Dark mode: reset all filters and blend mode
              "dark:hue-rotate-0 dark:saturate-100 dark:brightness-100 dark:mix-blend-normal"
            )}
          />
        </div>
      </div>
    </div>
  );
}
